import { type Common } from './common.js';
import { type PaginatedQueryOptions } from './models/pagination/PaginatedQueryOptions.type.js';
import { type PaginationResponse } from './models/pagination/PaginationResponse.type.js';
import {
  type CreateValues,
  type CreatedServiceAccount,
  type FindAllAvailableScopesOptions,
  type FindAllOptions,
  type Scope,
  type ServiceAccount,
  type ServiceAccountScope,
  type ServiceAccountWithScopes,
  type UpdateValues,
} from './models/serviceAccount.js';

export class API {
  common: Common;
  baseEndpoint = '/service-accounts';

  constructor(common: Common) {
    this.common = common;
  }

  async findAllAvailableScopes(options?: FindAllAvailableScopesOptions): Promise<Scope[]> {
    const url = this.common.constructPath(`${this.baseEndpoint}/scopes`, options);
    return await this.common.getJSON(url);
  }

  async findOneById(id: string): Promise<ServiceAccount> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${id}`);
    return await this.common.getJSON(url);
  }

  async findOneWithScopesById(id: string): Promise<ServiceAccountWithScopes> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${id}`, {
      withScopes: true,
    });
    return await this.common.getJSON(url);
  }

  async findOneByToken(token: string, options: { deleted?: boolean }): Promise<ServiceAccount> {
    const url = this.common.constructPath(this.baseEndpoint, {
      token,
      ...options,
    });
    return await this.common.getJSON(url);
  }

  async findAll(options: FindAllOptions): Promise<ServiceAccount[]> {
    const url = this.common.constructPath(this.baseEndpoint, options);
    return await this.common.getJSON(url);
  }

  async findAllPaginated(
    options: PaginatedQueryOptions<FindAllOptions>
  ): Promise<PaginationResponse<ServiceAccountWithScopes>> {
    const url = this.common.constructPath(this.baseEndpoint, {
      ...options,
      withScopes: true,
    });
    return await this.common.getJSON(url);
  }

  async findAllWithScopes(options: FindAllOptions): Promise<ServiceAccountWithScopes[]> {
    const url = this.common.constructPath(this.baseEndpoint, {
      ...options,
      withScopes: true,
    });
    return await this.common.getJSON(url);
  }

  async create(values: CreateValues): Promise<CreatedServiceAccount> {
    const url = this.common.constructPath(this.baseEndpoint);
    return await this.common.postJSON(url, values);
  }

  async resetSecret(id: string): Promise<CreatedServiceAccount> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${id}/reset`);
    return await this.common.postJSON(url, {});
  }

  async update(id: string, values: UpdateValues): Promise<ServiceAccount> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${id}`);
    return await this.common.patchJSON(url, values);
  }

  async remove(id: string): Promise<void> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${id}`);
    return await this.common.deleteJSON(url);
  }

  async findAllScopes(id: string): Promise<ServiceAccountScope[]> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${id}/scopes`);
    return await this.common.getJSON(url);
  }

  async createScopes(id: string, scopes: Scope[]): Promise<ServiceAccountScope[]> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${id}/scopes`);
    return await this.common.postJSON(url, { scopes });
  }

  async removeScope(id: string, scopeId: string): Promise<void> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${id}/scopes/${scopeId}`);
    return await this.common.deleteJSON(url);
  }
}
